// Journal d'audit — et sa VÉRIFICATION, ici, dans le navigateur.
//
// C'est la promesse phare du produit : un journal opposable, exploitable en
// défense disciplinaire. Or un journal que seul le serveur peut vérifier ne vaut
// rien. Si l'on doit croire le serveur sur parole, autant qu'il n'y ait pas de
// signature du tout : celui qui falsifie le journal falsifie aussi le verdict.
//
// Tout est donc recalculé ICI :
//
//   • `entryHash = BLAKE2b-512(previousHash ‖ champs cadrés en longueur)` ;
//   • la signature Ed25519 de cet `entryHash` par la clé du journal ;
//   • le chaînage : chaque entrée pointe le hash de la précédente, index contigus.
//
// Et avec des implémentations **indépendantes** de celles du serveur (@noble,
// audité) : rejouer le code du serveur ne prouverait pas grand-chose.
//
// Reste la question qui décide de tout : **d'où vient la clé publique ?** Le
// serveur la donne. Un serveur qui réécrit son journal peut donc aussi fournir la
// clé qui va avec, et tout « vérifiera ». On l'ÉPINGLE donc à la première
// consultation, chiffrée sous la clé maîtresse — hors de portée du serveur — et on
// hurle si elle change.

import { ed25519 } from '@noble/curves/ed25519.js';
import { blake2b } from '@noble/hashes/blake2.js';
import { api } from '../api/client';
import { decryptBlob, encryptBlob, loadCrypto } from '../crypto';

const enc = new TextEncoder();
const toBase64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const fromBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const fromHex = (s: string) =>
  Uint8Array.from(s.match(/../g) ?? [], (b) => parseInt(b, 16));
const toHex = (b: Uint8Array) =>
  Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');

/** Une entrée brute, telle que le serveur la détient. */
export type RawEntry = {
  chainIndex: string;
  tenantId: string;
  dossierId: string | null;
  userId: string | null;
  action: string;
  resourceId: string | null;
  resourceType: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  timestamp: string;
  previousHash: string; // hex
  entryHash: string; // hex
  signature: string; // base64
};

export type AuditBundle = {
  entries: RawEntry[];
  signingPublicKey: string; // SPKI DER base64
  scope: 'dossier' | 'tenant';
  dossierId?: string;
  tenantId?: string;
};

/** Le verdict — celui du navigateur, pas celui du serveur. */
export type Verdict = {
  hashValid: boolean;
  signatureValid: boolean;
  /** Chaînage avec l'entrée précédente du lot (si elles se suivent). */
  linkValid: boolean | null;
};

export type VerifiedEntry = RawEntry & { verdict: Verdict };

export type ChainVerdict = {
  entries: VerifiedEntry[];
  /** Tout est-il vérifié ? */
  valid: boolean;
  /** Le lot couvre-t-il une chaîne CONTIGUË (donc rien n'a pu être supprimé) ? */
  contiguous: boolean;
  /** Première anomalie rencontrée, en clair. */
  brokenAt?: { chainIndex: string; reason: string };
  fingerprint: string;
  pinned: PinState;
};

// ---------------------------------------------------------------------------
// Le calcul — il doit reproduire EXACTEMENT celui du serveur
// ---------------------------------------------------------------------------

/**
 * Cadre chaque champ par sa longueur avant de le hacher.
 *
 * Sans ce cadrage, deux entrées différentes pourraient produire la même
 * concaténation (« ab » + « c » et « a » + « bc ») et donc le même hash : on
 * pourrait forger une entrée qui « vérifie ». L'ordre des champs est figé.
 */
function computeHash(previousHash: Uint8Array, e: RawEntry): Uint8Array {
  const fields = [
    e.chainIndex,
    e.tenantId,
    e.dossierId,
    e.userId,
    e.action,
    e.resourceId,
    e.resourceType,
    e.ipAddress,
    e.userAgent,
    e.timestamp,
  ];

  const parts: Uint8Array[] = [previousHash];
  for (const f of fields) {
    const bytes = enc.encode(f ?? '');
    const len = new Uint8Array(4);
    new DataView(len.buffer).setUint32(0, bytes.length, false); // big-endian
    parts.push(len, bytes);
  }

  const total = parts.reduce((n, p) => n + p.length, 0);
  const buf = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    buf.set(p, at);
    at += p.length;
  }
  return blake2b(buf, { dkLen: 64 });
}

/** SPKI DER d'une clé Ed25519 = 12 octets d'en-tête + les 32 octets bruts. */
const ED25519_SPKI_PREFIX_LEN = 12;

function rawPublicKey(spkiBase64: string): Uint8Array {
  const der = fromBase64(spkiBase64);
  return der.slice(ED25519_SPKI_PREFIX_LEN);
}

/**
 * Vérifie un lot d'entrées. **Rien n'est cru sur parole.**
 *
 * `contiguous` dit ce que le lot permet de prouver. Une chaîne contiguë (celle du
 * cabinet) prouve qu'**aucune entrée n'a été supprimée** : les index se suivent et
 * chaque hash enchaîne le précédent. Un sous-ensemble (celui d'un dossier) ne
 * prouve que l'authenticité de ce qu'on détient — les entrées des autres dossiers
 * s'intercalent, on ne peut rien dire des trous. La différence est capitale en
 * défense, et l'interface la dit.
 */
export function verifyBundle(bundle: AuditBundle, pinned: PinState): ChainVerdict {
  const publicKey = rawPublicKey(bundle.signingPublicKey);
  const entries: VerifiedEntry[] = [];

  let broken: ChainVerdict['brokenAt'];
  let contiguous = true;
  let previous: RawEntry | null = null;

  for (const e of bundle.entries) {
    const prevHash = fromHex(e.previousHash);
    const recomputed = computeHash(prevHash, e);
    const hashValid = toHex(recomputed) === e.entryHash;

    let signatureValid = false;
    try {
      signatureValid = ed25519.verify(
        fromBase64(e.signature),
        fromHex(e.entryHash),
        publicKey,
      );
    } catch {
      signatureValid = false;
    }

    // Le chaînage ne se vérifie qu'entre deux entrées CONSÉCUTIVES du journal.
    let linkValid: boolean | null = null;
    if (previous) {
      const suivies =
        BigInt(e.chainIndex) === BigInt(previous.chainIndex) + 1n;
      if (suivies) {
        linkValid = e.previousHash === previous.entryHash;
      } else {
        // Trou : d'autres dossiers se sont intercalés. Ce lot ne prouve donc
        // pas l'exhaustivité.
        contiguous = false;
      }
    }

    if (!broken) {
      if (!hashValid) broken = { chainIndex: e.chainIndex, reason: 'hash altéré' };
      else if (!signatureValid)
        broken = { chainIndex: e.chainIndex, reason: 'signature invalide' };
      else if (linkValid === false)
        broken = { chainIndex: e.chainIndex, reason: 'chaînage rompu' };
    }

    entries.push({ ...e, verdict: { hashValid, signatureValid, linkValid } });
    previous = e;
  }

  // Une chaîne complète part de l'index 0 : sinon, le début a disparu.
  const first = bundle.entries[0];
  if (bundle.scope === 'tenant' && first && first.chainIndex !== '0') {
    contiguous = false;
    broken ??= {
      chainIndex: first.chainIndex,
      reason: 'chaîne tronquée au début',
    };
  }

  return {
    entries,
    valid: !broken,
    contiguous: bundle.scope === 'tenant' ? contiguous : false,
    brokenAt: broken,
    fingerprint: keyFingerprint(publicKey),
    pinned,
  };
}

/** Empreinte lisible de la clé du journal — à comparer entre confrères. */
export function keyFingerprint(publicKey: Uint8Array): string {
  return toHex(blake2b(publicKey, { dkLen: 32 }).slice(0, 8))
    .replace(/(..)/g, '$1 ')
    .trim();
}

// ---------------------------------------------------------------------------
// Épinglage de la clé — sans lui, tout ce qui précède est décoratif
// ---------------------------------------------------------------------------

export type PinState = 'first-seen' | 'match' | 'CHANGED';

const PIN_AAD = enc.encode('delo/audit-pubkey-pin/v1');
const pinKey = (tenantSlug: string) => `delo.auditPin.${tenantSlug}`;

/**
 * Compare la clé annoncée par le serveur à celle qu'on avait épinglée.
 *
 * L'épingle est **chiffrée sous la clé maîtresse** : le serveur ne peut ni la lire
 * ni la remplacer (l'AEAD le démasquerait). C'est ce qui donne sa valeur à tout le
 * reste — sans épingle, un serveur qui réécrit son journal fournirait simplement la
 * clé publique correspondante, et la vérification passerait sans broncher.
 */
export function checkPin(
  tenantSlug: string,
  masterKey: Uint8Array,
  signingPublicKey: string,
): PinState {
  const stored = localStorage.getItem(pinKey(tenantSlug));
  if (!stored) {
    localStorage.setItem(
      pinKey(tenantSlug),
      toBase64(encryptBlob(masterKey, enc.encode(signingPublicKey), PIN_AAD)),
    );
    return 'first-seen';
  }
  try {
    const known = new TextDecoder().decode(
      decryptBlob(masterKey, fromBase64(stored), PIN_AAD),
    );
    return known === signingPublicKey ? 'match' : 'CHANGED';
  } catch {
    // Épingle illisible (autre clé maîtresse ?) : on ne prétend pas qu'elle
    // concorde. On la remplace et on repart d'une première vue.
    localStorage.removeItem(pinKey(tenantSlug));
    return checkPin(tenantSlug, masterKey, signingPublicKey);
  }
}

// ---------------------------------------------------------------------------
// Récupération et export
// ---------------------------------------------------------------------------

export async function fetchDossierAudit(
  token: string,
  dossierId: string,
): Promise<AuditBundle> {
  await loadCrypto();
  return api<AuditBundle>(`/dossiers/${dossierId}/audit`, { token });
}

export async function fetchTenantChain(token: string): Promise<AuditBundle> {
  await loadCrypto();
  return api<AuditBundle>('/audit/chain', { token });
}

/**
 * Exporte le journal, **avec de quoi le vérifier sans nous**.
 *
 * C'est la pièce qu'on produit devant le bâtonnier : elle contient les entrées
 * brutes, la clé publique, et la recette exacte du hash. Un expert peut la
 * recalculer avec n'importe quel outil — il n'a besoin ni de Delo, ni du serveur,
 * ni de notre bonne foi.
 */
export function exportBundle(bundle: AuditBundle, verdict: ChainVerdict): void {
  const document = {
    format: 'delo-audit-export/v1',
    exportedAt: new Date().toISOString(),
    scope: bundle.scope,
    dossierId: bundle.dossierId ?? null,
    tenantId: bundle.tenantId ?? null,
    signingPublicKey: bundle.signingPublicKey,
    signingPublicKeyFingerprint: verdict.fingerprint,
    verification: {
      hash: 'BLAKE2b-512(previousHash ‖ pour chaque champ : uint32be(longueur) ‖ utf8(valeur))',
      champs: [
        'chainIndex',
        'tenantId',
        'dossierId',
        'userId',
        'action',
        'resourceId',
        'resourceType',
        'ipAddress',
        'userAgent',
        'timestamp',
      ],
      nul: 'un champ absent est haché comme une chaîne vide',
      signature: 'Ed25519 sur entryHash, clé publique ci-dessus (SPKI DER, base64)',
      genese: 'previousHash de la première entrée = 64 octets nuls',
      chainage:
        'previousHash[n] = entryHash[n-1] ; chainIndex contigus à partir de 0',
    },
    entries: bundle.entries,
  };

  const blob = new Blob([JSON.stringify(document, null, 2)], {
    type: 'application/json',
  });
  const href = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = href;
  a.download =
    bundle.scope === 'tenant'
      ? 'delo-journal-cabinet.json'
      : `delo-journal-dossier-${bundle.dossierId}.json`;
  a.style.display = 'none';
  window.document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(href);
  }, 60_000);
}

// ---------------------------------------------------------------------------
// Vocabulaire
// ---------------------------------------------------------------------------

/** Les actions, en français. Une valeur inconnue reste affichée telle quelle. */
const LIBELLES: Record<string, string> = {
  connexion: 'Connexion',
  activation_2fa: 'Activation du second facteur',
  desactivation_2fa: 'Désactivation du second facteur',
  enrolement_cle_maitresse: 'Dépôt de la clé maîtresse (enveloppée)',
  changement_mot_de_passe: 'Changement de mot de passe',
  emission_invitation: 'Invitation émise',
  enrolement_compte: 'Compte activé',
  demande_appareil: 'Nouvel appareil : demande',
  approbation_appareil: 'Nouvel appareil : approuvé',
  reprise_appareil: 'Reprise d’un appareil perdu',
  configuration_recuperation: 'Mise en place de la récupération (Shamir)',
  verification_recuperation: 'Reconstitution de la clé prouvée',
  acceptation_part: 'Part de récupération acceptée',
  demande_recuperation: 'Récupération demandée',
  liberation_part: 'Part de récupération libérée',
  recuperation_compte: 'Compte récupéré',
  ouverture_dossier: 'Dossier ouvert',
  consultation_dossier: 'Dossier consulté',
  modification_dossier: 'Dossier modifié',
  envoi_message: 'Message envoyé',
  depot_document: 'Pièce déposée',
  telechargement_document: 'Pièce téléchargée',
  suppression_document: 'Pièce supprimée',
  ajout_participant: 'Participant ajouté',
  modification_permissions: 'Droits modifiés',
  retrait_participant: 'Participant retiré',
  creation_evenement: 'Échéance posée',
  modification_evenement: 'Échéance modifiée',
  suppression_evenement: 'Échéance retirée',
};

export const libelleAction = (action: string) => LIBELLES[action] ?? action;
